import { useTranslation } from "react-i18next";
import { DeleteOutlined, PaperClipOutlined, ReloadOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import {
  ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS,
  type DocumentUploadValidationError,
  resolveDocumentUploadValidationErrorTranslation,
} from "@av/apps/recruitment/ats-common/services/document-upload-validation";
import { Button } from "@av/components/atoms/Button";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Upload } from "@av/components/atoms/upload/Upload";
import { MiddleEllipsisFileName } from "./MiddleEllipsisFileName";

import type { UploadFile } from "antd/es/upload/interface";

const useStyles = createStyles(({ css, token }) => ({
  row: css`
    width: 100%;
    min-width: 0;
  `,
  fileDetails: css`
    min-width: 0;
    flex: 1 1 auto;
  `,
  fileSummary: css`
    width: 100%;
    min-width: 0;
    min-height: 28px;
  `,
  fileName: css`
    min-width: 0;
    max-width: 100%;
    flex: 0 1 auto;
  `,
  fullWidthFileName: css`
    flex: 1 1 0;
  `,
  paperClipIcon: css`
    flex: 0 0 auto;
    margin-top: 6px;
    font-size: 16px;
    line-height: 1;
    color: ${token.colorPrimary};
  `,
  actions: css`
    flex: 0 0 auto;
  `,
  iconButton: css`
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
  `,
  replaceButton: css`
    color: ${token.colorPrimary};
    background: ${token.colorPrimaryBg};
  `,
  removeButton: css`
    color: ${token.colorText};
  `,
}));

type UploadedDocumentFileRowProps = {
  file: UploadFileExtended;
  fullWidth?: boolean;
  validationError?: DocumentUploadValidationError;
  validationErrorId?: string;
  onReplace: (file: UploadFile) => void;
  onRemove: () => void;
};

export function UploadedDocumentFileRow({
  file,
  fullWidth = false,
  validationError,
  validationErrorId,
  onReplace,
  onRemove,
}: UploadedDocumentFileRowProps) {
  const { t } = useTranslation();
  const { cx, styles } = useStyles();

  return (
    <Flex className={styles.row} gap={8} align="flex-start">
      <PaperClipOutlined className={styles.paperClipIcon} aria-hidden />
      <Flex vertical className={styles.fileDetails}>
        <Flex className={styles.fileSummary} align="center" gap={30}>
          <Flex className={cx(styles.fileName, fullWidth && styles.fullWidthFileName)}>
            <MiddleEllipsisFileName
              fileName={file.name}
              testIdPrefix={`supporting-document-${file.uid}-name`}
            />
          </Flex>
          <Flex className={styles.actions} align="center" gap={4}>
            <Upload
              accept={ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS.join(",")}
              beforeUpload={(replacementFile) => {
                onReplace(replacementFile);

                return Upload.LIST_IGNORE;
              }}
              maxCount={1}
              showUploadList={false}
            >
              <Button
                testIdPrefix={`supporting-document-${file.uid}-replace`}
                className={`${styles.iconButton} ${styles.replaceButton}`}
                type="text"
                icon={<ReloadOutlined />}
                aria-label={t("recruitment.jobApplication.documents_replaceDocument", {
                  fileName: file.name,
                })}
              />
            </Upload>
            <Button
              testIdPrefix={`supporting-document-${file.uid}-remove`}
              className={`${styles.iconButton} ${styles.removeButton}`}
              type="text"
              icon={<DeleteOutlined />}
              aria-label={t("recruitment.jobApplication.documents_removeDocument", {
                fileName: file.name,
              })}
              onClick={onRemove}
            />
          </Flex>
        </Flex>
        {validationError ? (
          <Typography.Text
            id={validationErrorId}
            testIdPrefix={`supporting-document-${file.uid}-error`}
            role="alert"
            type="danger"
          >
            {resolveDocumentUploadValidationErrorTranslation(t, validationError)}
          </Typography.Text>
        ) : null}
      </Flex>
    </Flex>
  );
}
